
import React from "react";
import { cn } from "@/lib/utils";

// Typography variants for headings
export const headingVariants = {
  h1: "text-4xl font-bold tracking-tight",
  h2: "text-3xl font-semibold tracking-tight",
  h3: "text-2xl font-semibold",
  h4: "text-xl font-semibold",
  h5: "text-lg font-medium",
  h6: "text-base font-medium",
};

// Typography variants for body text
export const textVariants = {
  lead: "text-xl text-muted-foreground",
  large: "text-lg font-semibold",
  default: "text-base text-foreground",
  medium: "text-sm text-foreground",
  small: "text-xs font-medium text-muted-foreground",
  muted: "text-sm text-muted-foreground",
  brand: "text-nation-purple",
};

// Typography components
export function Heading({ 
  variant = "h2", 
  className, 
  children, 
  ...props 
}: { 
  variant?: keyof typeof headingVariants; 
  className?: string; 
  children: React.ReactNode;
  [key: string]: any;
}) {
  const Component = variant as keyof JSX.IntrinsicElements;
  return (
    <Component 
      className={cn(headingVariants[variant], className)} 
      {...props}
    >
      {children}
    </Component>
  );
}

export function Text({ 
  variant = "default", 
  className, 
  children, 
  ...props 
}: { 
  variant?: keyof typeof textVariants; 
  className?: string; 
  children: React.ReactNode;
  [key: string]: any;
}) {
  return (
    <p 
      className={cn(textVariants[variant], className)} 
      {...props}
    >
      {children}
    </p>
  );
}

// Typography utility for consistent text styling
export function typography(variant: keyof typeof headingVariants | keyof typeof textVariants) {
  if (variant in headingVariants) {
    return headingVariants[variant as keyof typeof headingVariants];
  }
  return textVariants[variant as keyof typeof textVariants];
}
